import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articleData } from '@/mocks/articles';
import { videoCategories, allVideos } from '@/mocks/videos';

export default function Article() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const article = id ? articleData[id] : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="w-full px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">内容未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该内容可能已被移除或链接无效</p>
            <button
              onClick={() => navigate('/')}
              className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('notFound.backHome')}
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryInfo = videoCategories.find((c) => c.slug === article.category);
  const relatedVideos = allVideos
    .filter((v) => v.category === article.category && v.id !== article.id)
    .slice(0, 4);

  const breadcrumbItems = [
    { label: '分类浏览', path: '/category/all' },
    { label: categoryInfo?.name || article.category, path: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.director,
    },
    publisher: {
      '@type': 'Organization',
      name: '豆花视频',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.douhuavideo.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '分类浏览',
        item: 'https://www.douhuavideo.com/category/all',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: categoryInfo?.name || article.category,
        item: `https://www.douhuavideo.com/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: article.title,
      },
    ],
  };

  const paragraphs = article.content.split('\n\n').filter(Boolean);

  return (
    <>
      <SchemaOrg type="Article" data={articleSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <article>
              <header className="mb-6 md:mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <Link
                    to={`/category/${article.category}`}
                    className="text-xs px-2.5 py-1 bg-primary-50 text-primary-600 rounded-full font-medium hover:bg-primary-100 transition-colors duration-200 cursor-pointer"
                  >
                    {categoryInfo?.name || article.category}
                  </Link>
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2 py-0.5 bg-background-100 text-foreground-500 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万次观看` : `${article.views}次观看`}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    {article.rating} 分
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.duration}
                  </span>
                </div>
              </header>

              <div className="relative overflow-hidden rounded-lg aspect-video mb-8">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4 md:p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-500 text-background-50 cursor-pointer hover:bg-primary-600 transition-colors duration-200">
                      <i className="ri-play-fill text-2xl"></i>
                    </div>
                    <div>
                      <p className="text-white text-sm md:text-base font-medium">{article.title}</p>
                      <p className="text-white/70 text-xs">{article.duration}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-6 mb-8">
                <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-3">内容简介</h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {article.description}
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4 mb-8 p-4 bg-background-100 rounded-lg">
                <div>
                  <span className="text-xs text-foreground-500">导演</span>
                  <p className="text-sm font-medium text-foreground-800">{article.director}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">年份</span>
                  <p className="text-sm font-medium text-foreground-800">{article.year}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">地区</span>
                  <p className="text-sm font-medium text-foreground-800">{article.region}</p>
                </div>
                <div className="col-span-2 sm:col-span-3">
                  <span className="text-xs text-foreground-500">主演</span>
                  <p className="text-sm font-medium text-foreground-800">{article.cast.join(' / ')}</p>
                </div>
              </div>

              <div className="prose-custom mb-10">
                {paragraphs.map((paragraph, index) => (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    {paragraph.trim()}
                  </p>
                ))}
              </div>
            </article>

            {relatedVideos.length > 0 && (
              <section className="border-t border-background-200 pt-8 mb-12">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  {t('article.related')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {relatedVideos.map((video) => (
                    <Link
                      key={video.id}
                      to={`/article/${video.id}`}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative overflow-hidden aspect-video">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                          {video.duration}
                        </div>
                      </div>
                      <div className="p-3">
                        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                          {video.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}